import { ArrowBack, CheckCircle, ExpandMore, NavigateBefore, NavigateNext } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Grid, Step, StepLabel, Stepper, Typography } from '@mui/material'
import { GreenText, LabelField, PhotoCard } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { MichiganSOSBundleDto, MichiganSOSBundleTitleApplicationDeliveryPhotoDto } from 'dtos'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatDateMoment, formatTimeMoment, isPast } from 'utils'
import TitleApplicationDeliveryPhotoDialog from './TitleApplicationDeliveryPhotoDialog'
import { Form, Formik } from 'formik'
import { enqueueSnackbar } from 'notistack'

export default function NeedsToBeTakenToSecretaryOfState() {
  const { michiganSOSBundleGuid } = useParams<{ michiganSOSBundleGuid: string }>()
  const layout = useLayout()
  const navigate = useNavigate()

  const [activeStep, setActiveStep] = useState<number>(0)
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<MichiganSOSBundleTitleApplicationDeliveryPhotoDto>(new MichiganSOSBundleTitleApplicationDeliveryPhotoDto())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [michiganSoSBundle, setMichiganSoSBundle] = useState<MichiganSOSBundleDto>(new MichiganSOSBundleDto())

  useEffect(() => {
    if (michiganSOSBundleGuid) {
      layout.setIsLayoutLoading(true)
      controllers.michiganSoSBundles
        .getMichiganSOSBundle(michiganSOSBundleGuid)
        .then(res => {
          setMichiganSoSBundle(res.value)
        })
        .finally(() => {
          layout.setIsLayoutLoading(false)
        })
    }
  }, [michiganSOSBundleGuid])

  return (
    <Grid container spacing={1} alignItems='center'>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container justifyContent='space-between'>
              <Grid item>
                <Typography fontWeight='bold' variant='h5'>
                  Title Application Delivery
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  color='primary'
                  onClick={() => {
                    navigate(-1)
                  }}
                  startIcon={<ArrowBack />}
                  variant='text'
                >
                  BACK
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <Accordion variant='outlined'>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography fontWeight='bold' variant='h5'>
                      {michiganSoSBundle.michiganTitleOwnerName}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Grid container spacing={1} alignItems='center'>
                      <Grid item xs={12}>
                        <LabelField
                          label='Drop Off Date'
                          value={formatDateMoment(michiganSoSBundle.dropOffDate)}
                          error={isPast(michiganSoSBundle.dropOffDate)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <LabelField label='Time' value={formatTimeMoment(michiganSoSBundle.dropOffTime)} />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Drop Off Information Card Accordion:**

Clicking the Drop Off Information card will expanding the accordion displaying additional details about the Title Application Delivery

**Accordion Heading:**

Displays the Owner Name from the Michigan SoS Bundles.

**Drop Off Date:**

Displays the Drop Off Date from the Michigan SoS Bundles. 

The Drop Off Date value is displayed in bold and red if the date is in the past.

**Time:**

Displays the Time from the Michigan SoS Bundles.`}
                </GreenText>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values, formikHelpers) => {
            navigate(-1)
          }}
          validateOnBlur
          validateOnChange
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form>
              <Card>
                <CardContent>
                  <Grid container spacing={1} alignItems='center'>
                    <Grid item xs={12}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        <Step>
                          <StepLabel>Title Application Delivery Photo</StepLabel>
                        </Step>

                        <Step>
                          <StepLabel>Title Application Delivery Review</StepLabel>
                        </Step>
                      </Stepper>
                    </Grid>

                    {activeStep === 0 && (
                      <>
                        <TitleApplicationDeliveryPhotoDialog
                          initialValues={values}
                          isLoading={isLoading}
                          onClose={() => {
                            setDialogOpen(false)
                          }}
                          onSave={values => {
                            setIsLoading(true)
                            controllers.michiganSoSBundles
                              .uploadReplaceTitleApplicationDeliveryPhoto(michiganSoSBundle.michiganSOSBundleGuid!, values)
                              .then(res => {
                                setDialogOpen(false)
                                enqueueSnackbar('Title Application Delivery Photo Uploaded Successfully!', { variant: 'success' })

                                setFieldValue('titleApplicationDeliveryPhotoFile', values.titleApplicationDeliveryPhotoFile)
                              })
                              .finally(() => {
                                setIsLoading(false)
                              })
                          }}
                          open={dialogOpen}
                        />

                        <Grid item xs={12} container alignItems='center' justifyContent='center'>
                          <Grid item>
                            <PhotoCard
                              complete={Boolean(values.titleApplicationDeliveryPhotoFile)}
                              label='Title Application Delivery Photo'
                              src={values.titleApplicationDeliveryPhotoFile ? URL.createObjectURL(values.titleApplicationDeliveryPhotoFile) : undefined}
                              onClick={() => {
                                setDialogOpen(true)
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            disabled={!values.titleApplicationDeliveryPhotoFile}
                            fullWidth
                            endIcon={<NavigateNext />}
                            onClick={() => {
                              setActiveStep(prev => prev + 1)
                            }}
                            variant='contained'
                          >
                            NEXT
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          <GreenText variant='outlined'>
                            {`**Title Application Delivery Steps:**

Displays the current step of the user is on in the Title Application Delivery process.

**Title Application Delivery Photo:**

Displays the Title Application Delivery image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be displayed instead.

Clicking the Title Application Delivery image will open the Capture Cluster with Title Application Delivery Photo Dialog.

**NEXT Button:**

Clicking this button will:
   - Save the uploaded photo to the database.
   - Advance the user to the next step in the Title Application Delivery process.

This button remains disabled until a photo has been uploaded.`}
                          </GreenText>
                        </Grid>
                      </>
                    )}

                    {activeStep === 1 && (
                      <>
                        <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='center'>
                          <Grid item>
                            <CheckCircle color='success' />
                          </Grid>

                          <Grid item>
                            <Typography variant='body1'>Title Application Delivery Photo</Typography>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Button fullWidth type='submit' variant='contained'>
                            SUBMIT
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            startIcon={<NavigateBefore />}
                            onClick={() => {
                              setActiveStep(prev => prev - 1)
                            }}
                            variant='contained'
                          >
                            PREVIOUS
                          </Button>
                        </Grid>

                        <Grid item xs={12}>
                          <GreenText variant='outlined'>
                            {`**Title Application Delivery Steps:**

Displays the current step of the user is on in the Title Application Delivery process.

**Title Application Delivery Photo Check Mark**

Displays a check mark to indicate that the Title Application Delivery Photo has been successfully uploaded.

**SUBMIT Button:**

Clicking this button will:
  - Upload the photos. 
  - Pressing “Submit” uploads the Title Application Delivery Photo and associates it with the Michigan SoS Bundle. The upload is also linked to the user who completed the process, along with a date/time stamp.
  - Return the user to the "Michigan Titles – Needs To Be Taken To Secretary Of Sate" to-do list screen.

**PREVIOUS Button:**

Clicking this button will:
   - Return the user to the previous step in the process.`}
                          </GreenText>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  )
}
